import { Link } from "@tanstack/react-router";
import { Plus, Rss } from "lucide-react";
import { TabEmptyState } from "@/components/entity-tabs/tab-states";
import { useNotificationPeek } from "@/components/peek/notification-peek-context";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type ConnectionItem,
	type ConnectorDefinitionItem,
	type FeedItem,
	useFeeds,
} from "@/lib/api";
import { formatTimeAgo } from "@/lib/format-utils";
import { CapabilitySummary } from "./capability-browser";
import { StatusDot } from "./status-dot";

export function feedAttentionLabel(feed: FeedItem): string | null {
	switch (feed.attention) {
		case "needs_auth":
			return "Needs authentication";
		case "setup_required":
			return feed.attention_reason?.trim() || "Setup required";
		case "paused":
			return "Paused";
		case "misconfigured":
			return "Configuration error";
		case "device_offline":
			return `Waiting for ${feed.device_label || feed.device_platform || "device"}`;
		case "last_attempt_failed": {
			const failures = Number(feed.consecutive_failures ?? 0);
			return (
				feed.last_error?.trim() ||
				(failures > 0
					? `${failures} consecutive sync ${failures === 1 ? "failure" : "failures"}`
					: "Last sync failed")
			);
		}
		case "overdue":
			return "Scheduled run overdue";
		case "no_trigger":
			return "No schedule — this feed only runs when you sync it";
		case "never_run":
			return feed.execution_mode === "scheduled"
				? "Scheduled, not run yet"
				: "Not synced yet";
		default:
			return null;
	}
}

export function feedAttentionTone(
	feed: FeedItem,
): "amber" | "muted" | "destructive" | null {
	switch (feed.attention) {
		case "last_attempt_failed":
		case "misconfigured":
			return "destructive";
		case "needs_auth":
		case "setup_required":
		case "device_offline":
		case "overdue":
		case "no_trigger":
		case "never_run":
			return feed.attention === "never_run" &&
				feed.execution_mode !== "scheduled"
				? "muted"
				: "amber";
		case "paused":
			return "muted";
		default:
			return feed.status === "active"
				? null
				: feed.status === "error"
					? "destructive"
					: "muted";
	}
}

/** True when the connector publishes at least one configurable feed type. */
export function connectorHasFeedTypes(
	connectorDef?: ConnectorDefinitionItem | null,
): boolean {
	return Object.keys(connectorDef?.feeds_schema ?? {}).length > 0;
}

/**
 * The `Feeds` tab body on a connection: the feeds this connection actually has,
 * as opposed to the connector-level tab which lists what the connector offers.
 * Same tab, same position — the scope narrows as you descend.
 */
export function ConnectionFeedsList({
	connection,
	ownerSlug,
	connectorKey,
	connectorDef,
}: {
	connection: ConnectionItem;
	ownerSlug: string;
	connectorKey: string;
	connectorDef?: ConnectorDefinitionItem;
}) {
	const {
		data: feeds = [],
		isLoading,
		isError,
		refetch,
	} = useFeeds(connection.id);
	const canAddFeed = connectorHasFeedTypes(connectorDef);
	const isChat = connection.facets?.chat ?? false;

	const params = {
		owner: ownerSlug,
		connectorKey,
		connectionId: String(connection.id),
	};

	if (isLoading) {
		return (
			<div className="flex flex-col gap-2">
				<Skeleton className="h-12 w-full rounded-lg" />
				<Skeleton className="h-12 w-full rounded-lg" />
			</div>
		);
	}

	if (isError)
		return (
			<div role="alert" className="space-y-2">
				<p>Could not load feeds.</p>
				<Button variant="outline" size="sm" onClick={() => void refetch()}>
					Retry
				</Button>
			</div>
		);

	if (feeds.length === 0) {
		return (
			<TabEmptyState
				icon={Rss}
				title={isChat ? "No channels" : "No feeds"}
				description={
					canAddFeed
						? isChat
							? "Bind a channel to route conversations to this connection."
							: "Add a feed to start collecting data from this connection."
						: isChat
							? "This chat connection has no bound channels yet."
							: "This connector doesn't provide any feeds."
				}
			>
				{canAddFeed ? (
					<Button asChild size="sm">
						<Link
							to="/$owner/connectors/$connectorKey/$connectionId"
							params={params}
							search={{ addFeed: true }}
						>
							<Plus className="h-3.5 w-3.5" />
							{isChat ? "Add channel" : "Add feed"}
						</Link>
					</Button>
				) : null}
			</TabEmptyState>
		);
	}

	return (
		<ul className="flex flex-col gap-2">
			{feeds.map((feed) => {
				const feedName = feed.display_name?.trim() || feed.feed_key;
				const attentionLabel = feedAttentionLabel(feed);
				return (
					<li key={feed.id}>
						<Link
							to="/$owner/connectors/$connectorKey/$connectionId"
							params={params}
							search={{ feedId: feed.id }}
							className="flex w-full items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 text-left transition-colors hover:bg-accent/50"
						>
							<span className="flex min-w-0 items-center gap-2">
								<StatusDot tone={feedAttentionTone(feed)} />
								<span className="min-w-0 truncate text-sm font-medium">
									{feedName}
								</span>
							</span>
							<span
								className={
									feed.attention === "last_attempt_failed" ||
									feed.attention === "misconfigured"
										? "max-w-[50%] shrink-0 text-right text-xs text-destructive"
										: feed.attention && feed.attention !== "healthy"
											? "max-w-[50%] shrink-0 text-right text-xs text-amber-600 dark:text-amber-400"
											: "shrink-0 text-xs text-muted-foreground"
								}
								title={attentionLabel ?? undefined}
							>
								{attentionLabel ??
									(feed.store === "channel_messages"
										? "chat channel"
										: feed.operations.includes("read") &&
												feed.operations.includes("sync")
											? "synced + live"
											: feed.operations.includes("read")
												? "live"
												: feed.last_sync_at
													? `synced ${formatTimeAgo(feed.last_sync_at)}`
													: "not synced yet")}
							</span>
						</Link>
					</li>
				);
			})}
		</ul>
	);
}

/** Keep active source problems visible while the full list lives in the peek. */
export function ConnectionFeedsSummary({
	connection,
	ownerSlug,
	title,
}: {
	connection: ConnectionItem;
	ownerSlug: string;
	title: string;
}) {
	const peek = useNotificationPeek();
	const { data: feeds = [], isLoading, isError } = useFeeds(connection.id);
	const attention = feeds.filter((feed) =>
		[
			"needs_auth",
			"setup_required",
			"misconfigured",
			"last_attempt_failed",
			"device_offline",
			"overdue",
		].includes(feed.attention ?? ""),
	);
	const paused = feeds.filter((feed) => feed.status === "paused").length;
	const count = isLoading ? connection.feed_count : feeds.length;
	const noun = connection.facets?.chat ? "channels" : "feeds";
	const summary = isLoading
		? `Loading ${noun}…`
		: isError
			? `Could not load ${noun} — open to retry`
			: `${count} ${noun}${attention.length ? ` · ${attention.length} need attention` : ""}${paused ? ` · ${paused} paused` : ""}`;
	return (
		<div className="space-y-2">
			<CapabilitySummary
				title={title}
				summary={summary}
				onOpen={() =>
					peek?.open({
						href: "",
						title: connection.facets?.chat ? "Channels" : "Feeds",
						connectorPanel: {
							section: "feeds",
							connectorKey: connection.connector_key,
							connectionId: connection.id,
							organizationId: connection.organization_id,
							ownerSlug,
						},
					})
				}
			/>
			{attention.length > 0 && (
				<ul className="space-y-1 text-xs text-destructive">
					{attention.slice(0, 3).map((feed) => (
						<li key={feed.id}>
							{feed.display_name || feed.feed_key}: {feedAttentionLabel(feed)}
						</li>
					))}
					{attention.length > 3 && (
						<li>{attention.length - 3} more sources need attention.</li>
					)}
				</ul>
			)}
		</div>
	);
}
